import { createStyles } from 'antd-style';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const useStyles = createStyles(({ token, css }) => ({
  root: css`
    color: ${token.colorText};
    font-size: 13px;
    line-height: 1.72;
    word-break: break-word;

    > :first-child {
      margin-top: 0;
    }

    > :last-child {
      margin-bottom: 0;
    }

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      color: ${token.colorTextHeading};
      font-weight: 650;
      line-height: 1.35;
      margin: 18px 0 10px;
    }

    h1 {
      font-size: 22px;
    }

    h2 {
      font-size: 18px;
      padding-bottom: 6px;
      border-bottom: 1px solid ${token.colorBorderSecondary};
    }

    h3 {
      font-size: 16px;
    }

    h4,
    h5,
    h6 {
      font-size: 14px;
    }

    p,
    ul,
    ol,
    blockquote,
    pre,
    table {
      margin: 0 0 12px;
    }

    ul,
    ol {
      padding-left: 22px;
    }

    li + li {
      margin-top: 4px;
    }

    blockquote {
      border-left: 3px solid ${token.colorBorder};
      background: ${token.colorFillQuaternary};
      color: ${token.colorTextSecondary};
      padding: 8px 12px;
      border-radius: ${token.borderRadiusSM}px;
    }

    code {
      background: ${token.colorFillSecondary};
      border: 1px solid ${token.colorBorderSecondary};
      border-radius: 4px;
      color: ${token.colorTextHeading};
      font-family: ${token.fontFamilyCode};
      font-size: 12px;
      padding: 1px 5px;
    }

    pre {
      background: ${token.colorBgLayout};
      border: 1px solid ${token.colorBorderSecondary};
      border-radius: ${token.borderRadius}px;
      overflow: auto;
      padding: 12px;
    }

    pre code {
      background: transparent;
      border: 0;
      padding: 0;
      white-space: pre;
    }

    table {
      width: 100%;
      border-collapse: collapse;
      display: block;
      overflow: auto;
    }

    th,
    td {
      border: 1px solid ${token.colorBorderSecondary};
      padding: 6px 8px;
    }

    th {
      background: ${token.colorFillQuaternary};
      color: ${token.colorTextHeading};
      font-weight: 600;
    }

    a {
      color: ${token.colorPrimary};
    }

    hr {
      border: 0;
      border-top: 1px solid ${token.colorBorderSecondary};
      margin: 16px 0;
    }
  `,
  empty: css`
    color: ${token.colorTextTertiary};
    font-size: 13px;
  `,
}));

export interface MarkdownViewProps {
  children?: string | null;
  className?: string;
  emptyText?: string;
}

export default function MarkdownView({
  children,
  className,
  emptyText = '暂无内容',
}: MarkdownViewProps) {
  const { styles, cx } = useStyles();
  const text = children || '';

  if (!text.trim()) {
    return <div className={cx(styles.empty, className)}>{emptyText}</div>;
  }

  return (
    <div className={cx(styles.root, className)}>
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
    </div>
  );
}
